import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePhotoHandler } from '../hooks/usePhotoHandler';
import './ConfirmViolationPage.css';
import { IllegalParkingReport } from "../types/illegalParkingReport";

interface LocationState {
  violation: IllegalParkingReport;
  isConfirmation: boolean;
}

const ConfirmViolationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState<'upload' | 'review' | 'success'>('upload');
  const {
    photos,
    uploadInputRef,
    handleFileSelect,
    handleRemovePhoto,
  } = usePhotoHandler();

  // Check if we have the required state
  const state = location.state as LocationState;
  const violation = state?.violation;

  useEffect(() => {
    // Clean up object URLs when the component unmounts
    return () => {
      photos.forEach(photo => URL.revokeObjectURL(photo.url));
    };
  }, [photos]);
  
  if (!violation || !state?.isConfirmation) {
    return (
      <div className="confirm-violation-page">
        <div className="error-container">
          <h2>Invalid Access</h2>
          <p>Please return to the community page to confirm a violation.</p>
          <button 
            className="action-btn primary"
            onClick={() => navigate('/community')}
          >
            Go to Community
          </button>
        </div>
      </div>
    );
  }

  const handleContinue = () => {
    if (photos.length > 0) {
      setCurrentStep('review');
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // TODO: Backend Integration
      // In production, this would:
      // 1. Upload photos to storage
      // 2. Call API to update violation status to 'confirmed'
      // 3. Send confirmation data to backend
      // 4. Refresh community page data
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setCurrentStep('success');
    } catch (error) {
      console.error('Error confirming violation:', error);
      alert('Failed to confirm violation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToCommunity = () => {
    navigate('/community');
  };

  if (currentStep === 'success') {
    return (
      <div className="confirm-violation-page">
        <div className="success-container">
          <div className="success-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>
          <h2>Violation Confirmed!</h2>
          <p>Thank you for helping keep our community safe. Your additional photo evidence has been submitted.</p>
          
          <div className="violation-summary">
            <h4>Confirmed Violation</h4>
            <p><strong>{violation.licensePlate}</strong> ({violation.state})</p>
            <p>{violation.reason} • {violation.address}</p>
          </div>
          
          <button 
            className="action-btn primary"
            onClick={handleBackToCommunity}
          >
            Return to Community
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="confirm-violation-page">
      <div className="page-header">
        <h1>Confirm Violation</h1>
        <p>Upload additional photos to confirm this vehicle is still parked illegally</p>
      </div>

      <div className="violation-details">
        <div className="vehicle-info">
          <h3>{violation.licensePlate} ({violation.state})</h3>
          <p className="violation-type">{violation.reason}</p>
          <p className="location">{violation.address}</p>
        </div>

        <div className="original-photos">
          <h4>Original Evidence ({violation.presignedUrlsForImages?.length || 0} photos)</h4>
          <div className="photo-grid">
            {violation.presignedUrlsForImages?.map((imageUrl, index) => (
              <div key={index} className="photo-item">
                <img src={imageUrl} alt={`Evidence ${index + 1}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {currentStep === 'upload' && (
        <div className="upload-section">
          <h3>Add New Photos</h3>
          <p>Take new photos to show the vehicle is still there. This provides evidence of duration.</p>
          
          <div className="upload-area">
            <input
              type="file"
              multiple
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="file-input"
              id="photo-upload"
              ref={uploadInputRef}
            />
            <label htmlFor="photo-upload" className="upload-button">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span>Take Photos</span>
            </label>
          </div>

          {photos.length > 0 && (
            <div className="new-photos">
              <h4>New Photos ({photos.length})</h4>
              <div className="photo-grid">
                {photos.map((photo, index) => (
                  <div key={index} className="photo-item">
                    <img src={photo.url} alt={`Confirmation ${index + 1}`} />
                    <button 
                      className="remove-photo"
                      onClick={() => handleRemovePhoto(photo.id)}
                      title="Remove photo"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="action-buttons">
            <button 
              className="action-btn secondary"
              onClick={() => navigate('/community')}
            >
              Cancel
            </button>
            <button 
              className="action-btn primary"
              onClick={handleContinue}
              disabled={photos.length === 0}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {currentStep === 'review' && (
        <div className="review-section">
          <h3>Review & Submit</h3>
          <p>Please review all photos before submitting your confirmation.</p>
          
          <div className="review-photos">
            <h4>Photos to Submit ({photos.length})</h4>
            <div className="photo-grid">
              {photos.map((photo, index) => (
                <div key={index} className="photo-item">
                  <img src={photo.url} alt={`Review ${index + 1}`} />
                  <button 
                    className="remove-photo"
                    onClick={() => handleRemovePhoto(photo.id)}
                    title="Remove photo"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="confirmation-note">
            <h4>What happens after submission?</h4>
            <ul>
              <li>Your photos will be added as evidence of duration</li>
              <li>The violation status will be updated to "confirmed"</li>
              <li>This strengthens the case for enforcement action</li>
              <li>The community will see the updated violation status</li>
            </ul>
          </div>

          <div className="action-buttons">
            <button 
              className="action-btn secondary"
              onClick={() => setCurrentStep('upload')}
            >
              Back
            </button>
            <button 
              className="action-btn primary"
              onClick={handleSubmit}
              disabled={isSubmitting || photos.length === 0}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Confirmation'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfirmViolationPage; 